import apiClient from '../../../lib/client';

export const civicAPI = {
  // Get all voting precincts/districts for an address (neighborhood → county → state).
  // Includes: school district, city, congressional/state legislative districts.
  // Powered by the US Census Bureau Geocoder — free, no API key required.
  getAddressPrecincts: async (address) => {
    const { data } = await apiClient.get(
      `/geography/address-precincts?address=${encodeURIComponent(address)}`
    );
    return data;
  },

  // Get all districts for a place + state.
  // Returns: place polygon boundary + all overlapping districts.
  // Powered by Nominatim (OSM) for place boundary + Census TIGERweb for districts.
  getPlaceDistricts: async (place, state) => {
    const { data } = await apiClient.get(
      `/geography/place-districts?place=${encodeURIComponent(place)}&state=${encodeURIComponent(state)}`
    );
    return data;
  },

  // Get voting districts (congressional, state senate/house) for an address.
  // Powered by the US Census Bureau Geocoder — free, no API key required.
  getDistricts: async (address) => {
    const { data } = await apiClient.get(
      `/geography/districts?address=${encodeURIComponent(address)}`
    );
    return data;
  },

  // Get a single election by id (with source history + related elections)
  getElection: async (id) => {
    const { data } = await apiClient.get(`/elections/${id}`);
    return data;
  },

  // Get top news stories for an election (Google News RSS, no API key needed)
  getElectionNews: async (id) => {
    const { data } = await apiClient.get(`/elections/${id}/news`);
    return data;
  },

  // Get list of elections, optionally filtered by state (e.g. { state: 'TX' })
  getElections: async (params = {}) => {
    const safeParams = params && !params.queryKey && !params.signal && !params.client ? params : {};

    const { data } = await apiClient.get('/elections', { params: safeParams });
    return data;
  },

  // Get polling locations by address
  getPollingLocations: async (address, electionId = null) => {
    const params = { address };
    if (electionId) params.electionId = electionId;

    const { data } = await apiClient.get('/elections/polling-locations', { params });
    return data;
  },

  // Get ballot information by address
  getBallot: async (address, electionId = null) => {
    const params = { address };
    if (electionId) params.electionId = electionId;

    const { data } = await apiClient.get('/elections/ballot', { params });
    return data;
  },

  // Get complete voter information (polling + ballot)
  getVoterInfo: async (address, electionId = null) => {
    const params = { address };
    if (electionId) params.electionId = electionId;

    const { data } = await apiClient.get('/elections/voter-info', { params });
    return data;
  },

  // Get district polygons (GeoJSON) for a lat/lon coordinate.
  // Returns: { congressional, senate, house, school } — each an array of GeoJSON features.
  getDistrictPolygons: async (lat, lon) => {
    const { data } = await apiClient.get(`/geography/district-polygons?lat=${lat}&lon=${lon}`);
    return data;
  },

  // Get representatives by address
  getRepresentatives: async (address, levels = null, roles = null) => {
    const params = { address };
    if (levels) params.levels = levels.join(',');
    if (roles) params.roles = roles.join(',');

    const { data } = await apiClient.get('/elections/representatives', { params });
    return data;
  },

  // Get detail for a specific district (congressional, senate, house, or school)
  // Returns: feature (GeoJSON), authorities, elections
  getDistrictDetail: async (type, state, num) => {
    const { data } = await apiClient.get('/civic/district-detail', {
      params: { type, state, num },
    });
    return data;
  },

  // Get all district polygons for an entire state.
  // Returns: stateFeature (state boundary), congressional, senate, house arrays of GeoJSON features.
  getStateMap: async (state) => {
    const { data } = await apiClient.get('/geography/state-map', { params: { state } });
    return data;
  },
};
